import axios from 'axios';
import toast from 'react-hot-toast';

import AuthInterceptorService from 'Services/AuthInterceptorService';
import { extractData, createErrorResponse } from 'Helpers/ApiHelpers';
import F2WCustomLogger from 'Services/F2WCustomLogger';

/**
 * Default error skip patterns — URL + status combinations where error handling
 * (toast notifications and logging) should be silently skipped.
 * These represent expected error responses that are handled elsewhere in the app.
 */
const DEFAULT_ERROR_SKIP_PATTERNS = [
  { urlPattern: 'check-tpp-assigment', status: 404 },
  { urlPattern: 'check-ec-assigment', status: 404 },
  { urlPattern: 'attachment/list', status: 404 },
  { urlPattern: 'flight-ticket/lock', status: 409 },
  { urlPattern: 'flight-ticket/release-lock', status: 409 },
  // FlightCard handles the 409 lock conflict on save/delete with its own
  // context-aware toast, so suppress the generic interceptor toast here.
  { urlPattern: 'flight-ticket/update', status: 409 },
  { urlPattern: 'flight-ticket/delete-flight', status: 409 },
];

class RestClientService {
  constructor(errorSkipPatterns = DEFAULT_ERROR_SKIP_PATTERNS) {
    this.client = axios.create();
    this.errorSkipPatterns = errorSkipPatterns;

    // Setup authentication interceptors
    AuthInterceptorService.setupRequestInterceptor(this.client);
    AuthInterceptorService.setupResponseInterceptor(this.client);

    // Setup error handling interceptor
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        // Only handle non-401 errors here (401 is handled by AuthInterceptorService)
        if (error.response?.status !== 401) {
          this.handleError(error);
        }
        return Promise.reject(error);
      }
    );
  }

  handleError(error) {
    const status = error.response?.status;
    const errorResponse = createErrorResponse(error);
    const { errorCode, message, traceId } = errorResponse;
    const url = error.config?.url || '';

    // Skip logging and toast for configured URL + status patterns
    const shouldSkip = this.errorSkipPatterns.some(
      (pattern) => status === pattern.status && url.includes(pattern.urlPattern)
    );
    if (shouldSkip) return;

    // Log structured error for debugging (only for unexpected errors)
    F2WCustomLogger.error('API Error', error, { status, errorCode, message, traceId, url });

    switch (status) {
      case 400:
        toast.error(`Bad Request: ${message}`);
        break;
      case 401:
        toast.error('Unauthorized. Please login again.');
        break;
      case 403:
        toast.error('Access forbidden.');
        break;
      case 404:
        toast.error('Resource not found.');
        break;
      case 500:
        toast.error('Server error. Please try again later.');
        break;
      default:
        toast.error(`Error: ${message}`);
    }
  }

  getBaseUrl() {
    const { getEnvironmentVariablesHandler } = require('Handlers/EnvHandler');
    const envVars = getEnvironmentVariablesHandler();
    return envVars.apiBaseUrl;
  }

  async Get(url, config = {}) {
    const fullUrl = `${this.getBaseUrl()}${url}`;
    const response = await this.client.get(fullUrl, config);
    return extractData(response.data);
  }

  async GetFromGpx(url, config = {}) {
    const { getEnvironmentVariablesHandler } = require('Handlers/EnvHandler');
    const envVars = getEnvironmentVariablesHandler();
    const fullUrl = `${envVars.apiGpxBaseUrl}${url}`;
    const response = await this.client.get(fullUrl, config);
    return extractData(response.data);
  }

  async Post(url, data, config = {}) {
    const fullUrl = `${this.getBaseUrl()}${url}`;
    const response = await this.client.post(fullUrl, data, {
      ...config,
      headers: {
        'Content-Type': 'application/json',
        ...config.headers,
      },
    });
    return extractData(response.data);
  }

  async Patch(url, data, config = {}) {
    const fullUrl = `${this.getBaseUrl()}${url}`;
    const response = await this.client.patch(fullUrl, data, config);
    return extractData(response.data);
  }

  async Delete(url, data = null, config = {}) {
    const fullUrl = `${this.getBaseUrl()}${url}`;
    const deleteConfig = data ? { ...config, data } : config;
    const response = await this.client.delete(fullUrl, deleteConfig);
    return extractData(response.data);
  }

  async Upload(url, file, formValues = []) {
    const fullUrl = `${this.getBaseUrl()}${url}`;
    const formData = new FormData();

    // Append formValues first
    if (Array.isArray(formValues)) {
      formValues.forEach(({ key, value }) => {
        if (value !== null && value !== undefined) {
          formData.append(key, value);
        }
      });
    } else if (formValues && typeof formValues === 'object') {
      Object.entries(formValues).forEach(([key, value]) => {
        if (key !== 'selectedFiles' && value !== null && value !== undefined) {
          formData.append(key, value);
        }
      });
    }

    // Append file(s) last
    if (file) {
      if (Array.isArray(file)) {
        file.forEach((f) => formData.append('file', f));
      } else {
        formData.append('file', file);
      }
    }

    const response = await this.client.post(fullUrl, formData, {
      headers: {
        // Don't set Content-Type - let browser set it with boundary
      },
    });
    return extractData(response.data);
  }

  async Download(url, data = {}, fileName = 'download') {
    const fullUrl = `${this.getBaseUrl()}${url}`;
    const response = await this.client.post(fullUrl, data, {
      responseType: 'blob',
    });

    const contentType = response.headers['content-type'] || 'application/octet-stream';
    const blob = new Blob([response.data], { type: contentType });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(link.href);

    return response.data;
  }
}

export default new RestClientService();
